import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Nav, Row, Spinner, Table } from 'react-bootstrap';
import Pagination from 'react-bootstrap/Pagination';
import Select from 'react-select';
import {
    clearLowStockOrdered,
    fetchAllProductsMinimal,
    fetchLowStockAlerts,
    fetchStoreProductSettings,
    fetchSuppliers,
    markLowStockOrdered,
    upsertStoreProductSetting,
    updateStoreProductSetting
} from '../../api/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faRotateLeft } from '@fortawesome/free-solid-svg-icons';


export default function StoreReorderLevels() {
    const navigate = useNavigate();
    const location = useLocation();
    const [allProducts, setAllProducts] = useState([]);
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [drafts, setDrafts] = useState({});
    const [message, setMessage] = useState(null);
    const [activeTab, setActiveTab] = useState(() => new URLSearchParams(location.search).get('tab') === 'alerts' ? 'alerts' : 'levels');
    const [alerts, setAlerts] = useState([]);
    const [alertsLoading, setAlertsLoading] = useState(false);
    const [suppliers, setSuppliers] = useState([]);
    const [alertFilters, setAlertFilters] = useState({
        search: '',
        status: '',
        ordered: '',
        supplier_id: '',
    });
    const [showSupplierOrderModal, setShowSupplierOrderModal] = useState(false);
    const [orderSupplierId, setOrderSupplierId] = useState('');
    const [orderProductSearch, setOrderProductSearch] = useState('');
    const [selectedAlertProductIds, setSelectedAlertProductIds] = useState([]);
    const supplierOptions = suppliers.map(supplier => ({value: String(supplier.id), label: supplier.supplier_name}));
    const visibleOrderAlerts = alerts.filter(alert => !alert.is_ordered &&
        orderProductSearch.trim().toLowerCase().split(/\s+/).every(term => (alert.product_name || '').toLowerCase().includes(term)));

    const handleBack = () => {
        navigate(-1);
    };

    // pagination (match purchase.js style)
    const [currentPage, setCurrentPage] = useState(1);
    const entriesPerPage = 20;

    const load = async () => {
        setLoading(true);
        try {
            const data = await fetchStoreProductSettings();
            setSettings(data.results || data);
        } catch (e) {
            console.error('Failed to load store product settings:', e);
            setSettings([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const loadProducts = async () => {
            try {
                const data = await fetchAllProductsMinimal();
                setAllProducts(Array.isArray(data) ? data : (data?.results || []));
            } catch (e) {
                console.error('Failed to load products:', e);
                setAllProducts([]);
            }
        };
        loadProducts();
    }, []);

    useEffect(() => {
        fetchSuppliers()
            .then((data) => setSuppliers(Array.isArray(data) ? data : (data?.results || [])))
            .catch((error) => {
                console.error('Failed to load suppliers:', error);
                setSuppliers([]);
            });
    }, []);

    const loadAlerts = async (filters = alertFilters) => {
        setAlertsLoading(true);
        try {
            const data = await fetchLowStockAlerts(filters);
            setAlerts(Array.isArray(data) ? data : (data?.results || []));
        } catch (error) {
            console.error('Failed to load stock alerts:', error);
            setAlerts([]);
        } finally {
            setAlertsLoading(false);
        }
    };

    useEffect(() => {
        loadAlerts(alertFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alertFilters.search, alertFilters.status, alertFilters.ordered, alertFilters.supplier_id]);

    const settingsByProductId = useMemo(() => {
        const map = new Map();
        settings.forEach((s) => map.set(String(s.product), s));
        return map;
    }, [settings]);

    const filteredProducts = useMemo(() => {
        const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
        if (!terms.length) return allProducts;
        return allProducts.filter((p) => {
            const label = (p.product_name || '').toLowerCase();
            return terms.every((term) => label.includes(term));
        });
    }, [allProducts, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const totalPages = Math.ceil(filteredProducts.length / entriesPerPage) || 1;
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const maxLinks = 5;
    let startLink = Math.max(currentPage - 2, 1);
    let endLink = Math.min(startLink + maxLinks - 1, totalPages);

    if (endLink > totalPages) {
        endLink = totalPages;
        startLink = Math.max(endLink - (maxLinks - 1), 1);
    }

    const pageNumbersToShow = [];
    for (let i = startLink; i <= endLink; i++) {
        pageNumbersToShow.push(i);
    }

    const paginatedProducts = useMemo(() => {
        const startIndex = (currentPage - 1) * entriesPerPage;
        return filteredProducts.slice(startIndex, startIndex + entriesPerPage);
    }, [currentPage, filteredProducts]);

    const getDisplayedValue = (product) => {
        const draft = drafts[String(product.id)];
        if (draft !== undefined) return draft;
        const setting = settingsByProductId.get(String(product.id));
        if (setting) return setting.reorder_level;
        return product.reorder_level ?? '';
    };

    const saveOne = async (product) => {
        const productId = String(product.id);
        const value = drafts[productId];
        if (value === undefined || value === '') {
            setMessage({ type: 'warning', text: 'Please enter a reorder level value before saving.' });
            return;
        }

        setSaving(true);
        setMessage(null);
        try {
            const existing = settingsByProductId.get(productId);
            if (existing?.id) {
                await updateStoreProductSetting(existing.id, { reorder_level: value });
            } else {
                await upsertStoreProductSetting(product.id, value);
            }
            await load();
            setMessage({ type: 'success', text: 'Reorder level saved.' });
        } catch (e) {
            console.error('Failed to save reorder level:', e);
            setMessage({ type: 'danger', text: 'Failed to save reorder level.' });
        } finally {
            setSaving(false);
        }
    };

    const openSupplierOrderModal = (productId = null) => {
        setSelectedAlertProductIds(productId ? [String(productId)] : []);
        setOrderSupplierId(alertFilters.supplier_id || '');
        setOrderProductSearch('');
        setShowSupplierOrderModal(true);
    };

    const toggleSelectedAlert = (productId) => {
        const key = String(productId);
        setSelectedAlertProductIds((prev) => (
            prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]
        ));
    };

    const submitSupplierOrder = async () => {
        if (!orderSupplierId) {
            setMessage({ type: 'warning', text: 'Select the supplier these items were ordered from.' });
            return;
        }
        if (!selectedAlertProductIds.length) {
            setMessage({ type: 'warning', text: 'Select at least one product ordered from this supplier.' });
            return;
        }

        try {
            await Promise.all(
                selectedAlertProductIds.map((productId) => markLowStockOrdered(productId, orderSupplierId))
            );
            setShowSupplierOrderModal(false);
            setSelectedAlertProductIds([]);
            await loadAlerts();
            setMessage({ type: 'success', text: 'Ordered products marked successfully.' });
        } catch (error) {
            console.error('Failed to mark ordered products:', error);
            setMessage({ type: 'danger', text: 'Failed to mark ordered products.' });
        }
    };

    const undoOrdered = async (productId) => {
        try {
            await clearLowStockOrdered(productId);
            await loadAlerts();
        } catch (error) {
            console.error('Failed to undo ordered product:', error);
            setMessage({ type: 'danger', text: 'Failed to undo ordered product.' });
        }
    };

    return (
        <div className="container-fluid py-4">
            <div className="back-button-container">
                <Button variant="danger" onClick={handleBack}>Back</Button>
            </div>
            <Row className="mb-3">
                <Col>
                    <h3 className="fw-bold mb-1">Reorder Levels (This Store)</h3>
                    <div className="text-muted">Set product reorder thresholds for your active store.</div>
                </Col>
            </Row>

            {message && (
                <Alert variant={message.type} onClose={() => setMessage(null)} dismissible>
                    {message.text}
                </Alert>
            )}

            <Card className="shadow-sm border-0">
                <Card.Body>
                    <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
                        <Nav.Item>
                            <Nav.Link eventKey="levels">Reorder Levels</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="alerts">Stock Alerts</Nav.Link>
                        </Nav.Item>
                    </Nav>

                    {activeTab === 'levels' && (
                    <>
                    <Row className="g-3 align-items-end mb-3">
                        <Col md={6}>
                            <Form.Label>Search product</Form.Label>
                            <Form.Control value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Type a product name..." />
                        </Col>
                        <Col md={6} className="text-md-end">
                            <Button variant="outline-secondary" onClick={load} disabled={loading || saving}>
                                Refresh
                            </Button>
                        </Col>
                    </Row>

                    {loading ? (
                        <Spinner animation="border" />
                    ) : (
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th style={{ width: 220 }}>Reorder Level</th>
                                    <th style={{ width: 140 }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedProducts.length ? paginatedProducts.map((p) => (
                                    <tr key={p.id}>
                                        <td>{p.product_name}</td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={getDisplayedValue(p)}
                                                onChange={(e) => setDrafts((prev) => ({ ...prev, [String(p.id)]: e.target.value }))}
                                            />
                                        </td>
                                        <td>
                                            <Button
                                                size="sm"
                                                variant="primary"
                                                disabled={saving}
                                                onClick={() => saveOne(p)}
                                            >
                                                Save
                                            </Button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={3} className="text-center text-muted">No products found</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    )}

                    <div className="mt-4 d-flex justify-content-center">
                        <Pagination>
                            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                            <Pagination.Prev
                                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                                disabled={currentPage === 1}
                            />
                            {pageNumbersToShow.map((page) => (
                                <Pagination.Item
                                    key={page}
                                    active={page === currentPage}
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            />
                            <Pagination.Last
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                            />
                        </Pagination>
                    </div>
                    </>
                    )}

                    {activeTab === 'alerts' && (
                        <>
                            <Row className="g-3 align-items-end mb-3">
                                <Col md={3}>
                                    <Form.Label>Search alert</Form.Label>
                                    <Form.Control
                                        value={alertFilters.search}
                                        onChange={(e) => setAlertFilters({...alertFilters, search: e.target.value})}
                                        placeholder="Any product words..."
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={alertFilters.status}
                                        onChange={(e) => setAlertFilters({...alertFilters, status: e.target.value})}
                                    >
                                        <option value="">All</option>
                                        <option value="critical">Critical</option>
                                        <option value="warning">Warning</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Label>Ordered</Form.Label>
                                    <Form.Select
                                        value={alertFilters.ordered}
                                        onChange={(e) => setAlertFilters({...alertFilters, ordered: e.target.value})}
                                    >
                                        <option value="">All</option>
                                        <option value="ordered">Ordered</option>
                                        <option value="unordered">Not Ordered</option>
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Supplier</Form.Label>
                                    <Form.Select
                                        value={alertFilters.supplier_id}
                                        onChange={(e) => setAlertFilters({...alertFilters, supplier_id: e.target.value})}
                                    >
                                        <option value="">All suppliers</option>
                                        {suppliers.map((supplier) => (
                                            <option key={supplier.id} value={supplier.id}>{supplier.supplier_name}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={2} className="text-md-end">
                                    <Button variant="primary" onClick={() => openSupplierOrderModal()}>
                                        Order From Supplier
                                    </Button>
                                </Col>
                            </Row>

                            {alertsLoading ? (
                                <Spinner animation="border" />
                            ) : (
                                <Table responsive hover>
                                    <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Current Stock</th>
                                        <th>Reorder Level</th>
                                        <th>Status</th>
                                        <th style={{ width: 140 }}>Action</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {alerts.length ? alerts.map((alert) => (
                                        <tr key={alert.product_id}>
                                            <td>
                                                <div>
                                                    {alert.product_name}
                                                    {alert.is_ordered && (
                                                        <FontAwesomeIcon icon={faCheck} className="text-success ms-2" />
                                                    )}
                                                </div>
                                                {alert.is_ordered && alert.supplier_name && (
                                                    <div className="small text-muted">Ordered from {alert.supplier_name}</div>
                                                )}
                                            </td>
                                            <td>{alert.stock}</td>
                                            <td>{alert.reorder_level}</td>
                                            <td>
                                                <Badge bg={alert.severity === 'critical' ? 'danger' : 'warning'}>
                                                    {alert.severity}
                                                </Badge>
                                            </td>
                                            <td>
                                                {!alert.is_ordered ? (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-success"
                                                        title="Mark ordered"
                                                        onClick={() => openSupplierOrderModal(alert.product_id)}
                                                    >
                                                        <FontAwesomeIcon icon={faCheck} />
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-secondary"
                                                        title="Undo ordered"
                                                        onClick={() => undoOrdered(alert.product_id)}
                                                    >
                                                        <FontAwesomeIcon icon={faRotateLeft} />
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={5} className="text-center text-muted">No stock alerts found</td>
                                        </tr>
                                    )}
                                    </tbody>
                                </Table>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showSupplierOrderModal} onHide={() => setShowSupplierOrderModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Mark Items Ordered From Supplier</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Supplier</Form.Label>
                        <Select
                            aria-label="Supplier for ordered items"
                            options={supplierOptions}
                            value={supplierOptions.find(option => option.value === String(orderSupplierId)) || null}
                            onChange={option => setOrderSupplierId(option?.value || '')}
                            isSearchable
                            isClearable
                            placeholder="Search supplier…"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="supplier-order-product-search">
                        <Form.Label>Filter products to mark</Form.Label>
                        <Form.Control type="search" placeholder="Type a product name…" value={orderProductSearch} onChange={event => setOrderProductSearch(event.target.value)} />
                        <Form.Text>{visibleOrderAlerts.length} products shown · {selectedAlertProductIds.length} selected. Selections are kept when you change the search.</Form.Text>
                    </Form.Group>
                    <Table responsive hover>
                        <thead>
                        <tr>
                            <th style={{ width: 60 }}>Pick</th>
                            <th>Product</th>
                            <th>Stock</th>
                            <th>Reorder Level</th>
                        </tr>
                        </thead>
                        <tbody>
                        {visibleOrderAlerts.map((alert) => (
                            <tr key={alert.product_id}>
                                <td>
                                    <Form.Check
                                        aria-label={`Mark ${alert.product_name}`}
                                        checked={selectedAlertProductIds.includes(String(alert.product_id))}
                                        onChange={() => toggleSelectedAlert(alert.product_id)}
                                    />
                                </td>
                                <td>{alert.product_name}</td>
                                <td>{alert.stock}</td>
                                <td>{alert.reorder_level}</td>
                            </tr>
                        ))}
                        {!visibleOrderAlerts.length && <tr><td colSpan={4} className="text-center text-muted">No unmarked products match this search.</td></tr>}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSupplierOrderModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={submitSupplierOrder}>Mark Ordered</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
